import { existsSync, readFileSync } from "fs";
import { resolve } from "path";
import { applyRuntimeEnvSnapshot, parseRuntimeEnvContent } from "./env-runtime.js";

type LoggerLike = Pick<Console, "log" | "warn">;
type WatchCallback = (eventType: string) => void;
type WatchLike = (path: string, options: { persistent: boolean }, listener: WatchCallback) => unknown;

type LoadRuntimeEnvSnapshotParams = {
  envRuntimePath: string;
  internalToken: string;
  runtimeEnvKeys: Set<string>;
  env?: NodeJS.ProcessEnv;
  existsSyncImpl?: typeof existsSync;
  readFileSyncImpl?: typeof readFileSync;
  parseRuntimeEnvContentImpl?: typeof parseRuntimeEnvContent;
  applyRuntimeEnvSnapshotImpl?: typeof applyRuntimeEnvSnapshot;
  logger?: LoggerLike;
};

type SetupRuntimeEnvHotReloadParams = {
  functionsPath: string;
  internalToken: string;
  isWarmWorker: boolean;
  env?: NodeJS.ProcessEnv;
  existsSyncImpl?: typeof existsSync;
  readFileSyncImpl?: typeof readFileSync;
  parseRuntimeEnvContentImpl?: typeof parseRuntimeEnvContent;
  applyRuntimeEnvSnapshotImpl?: typeof applyRuntimeEnvSnapshot;
  watchImpl?: WatchLike;
  setTimeoutImpl?: typeof setTimeout;
  clearTimeoutImpl?: typeof clearTimeout;
  setIntervalImpl?: typeof setInterval;
  clearIntervalImpl?: typeof clearInterval;
  logger?: LoggerLike;
};

export function resolveEnvRuntimePath(functionsPath: string): string {
  return resolve(functionsPath || "", "..", ".env.runtime");
}

export function loadRuntimeEnvSnapshot(params: LoadRuntimeEnvSnapshotParams): number {
  const env = params.env ?? process.env;
  const existsSyncImpl = params.existsSyncImpl ?? existsSync;
  const readFileSyncImpl = params.readFileSyncImpl ?? readFileSync;
  const parseRuntimeEnvContentImpl = params.parseRuntimeEnvContentImpl ?? parseRuntimeEnvContent;
  const applyRuntimeEnvSnapshotImpl = params.applyRuntimeEnvSnapshotImpl ?? applyRuntimeEnvSnapshot;
  const logger = params.logger ?? console;

  try {
    if (!existsSyncImpl(params.envRuntimePath)) {
      return 0;
    }

    const content = readFileSyncImpl(params.envRuntimePath, "utf-8");
    const vars = parseRuntimeEnvContentImpl(content, params.internalToken);
    if (vars === null) {
      logger.warn("[env] Cannot decrypt .env.runtime — GENCOW_INTERNAL_TOKEN missing");
      return 0;
    }

    const loaded = applyRuntimeEnvSnapshotImpl(env, params.runtimeEnvKeys, vars);
    if (loaded > 0) {
      logger.log(`[env] Loaded ${loaded} env var(s) from .env.runtime`);
    }
    return loaded;
  } catch (e: unknown) {
    logger.warn(`[env] Failed to load .env.runtime:`, (e as Error).message);
    return 0;
  }
}

export async function setupRuntimeEnvHotReload(params: SetupRuntimeEnvHotReloadParams): Promise<string> {
  const logger = params.logger ?? console;
  const existsSyncImpl = params.existsSyncImpl ?? existsSync;
  const setTimeoutImpl = params.setTimeoutImpl ?? setTimeout;
  const clearTimeoutImpl = params.clearTimeoutImpl ?? clearTimeout;
  const setIntervalImpl = params.setIntervalImpl ?? setInterval;
  const clearIntervalImpl = params.clearIntervalImpl ?? clearInterval;
  const runtimeEnvKeys = new Set<string>();
  const envRuntimePath = resolveEnvRuntimePath(params.functionsPath);
  const watchImpl = params.watchImpl ?? (await import("fs")).watch;

  const loadEnvRuntime = () =>
    loadRuntimeEnvSnapshot({
      envRuntimePath,
      internalToken: params.internalToken,
      runtimeEnvKeys,
      env: params.env,
      existsSyncImpl,
      readFileSyncImpl: params.readFileSyncImpl,
      parseRuntimeEnvContentImpl: params.parseRuntimeEnvContentImpl,
      applyRuntimeEnvSnapshotImpl: params.applyRuntimeEnvSnapshotImpl,
      logger,
    });

  const logWatchReady = () => {
    logger.log(
      params.isWarmWorker
        ? `[env] Watching .env.runtime for hot-reload in warm worker (inotify) ✓`
        : `[env] Watching .env.runtime for hot-reload (inotify) ✓`,
    );
  };

  const attachWatch = () => {
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    watchImpl(envRuntimePath, { persistent: false }, (eventType) => {
      if (eventType !== "change") return;
      if (debounceTimer) clearTimeoutImpl(debounceTimer);
      debounceTimer = setTimeoutImpl(() => {
        loadEnvRuntime();
        logger.log("[env] Hot-reloaded env vars from .env.runtime ✓");
      }, 100);
    });
  };

  if (!params.isWarmWorker) {
    loadEnvRuntime();
  }

  try {
    attachWatch();
    logWatchReady();
    return envRuntimePath;
  } catch {
    logger.log(`[env] .env.runtime not found yet — will load on next deploy`);

    const pollInterval = setIntervalImpl(async () => {
      if (!existsSyncImpl(envRuntimePath)) {
        return;
      }

      loadEnvRuntime();
      try {
        attachWatch();
        logWatchReady();
        clearIntervalImpl(pollInterval);
      } catch {
        /* retry next interval */
      }
    }, 60_000);

    return envRuntimePath;
  }
}
